/**
 * Maintenance-mode reload guard (FR-132, FR-133).
 *
 * When the SPA receives an HTTP 503, it reloads so the browser hits nginx and
 * is served the static maintenance page. This module holds the pure decision of
 * *whether* to reload, kept separate from `client.ts` so it can be unit-tested
 * directly (the client module is globally mocked in tests to avoid
 * `import.meta.env` issues).
 */

/** sessionStorage key holding the epoch-ms timestamp of the last maintenance reload. */
export const MAINTENANCE_RELOAD_KEY = 'maintenanceReloadAt';

/** sessionStorage key marking that maintenance mode is currently active (FR-137). */
export const MAINTENANCE_ACTIVE_KEY = 'maintenanceActive';

/** Minimum gap between maintenance reloads, guarding against reload loops. */
export const MAINTENANCE_RELOAD_MIN_INTERVAL_MS = 30_000;

/**
 * Decide whether to reload onto the nginx maintenance page in response to a 503.
 *
 * Guarded so a transient backend 503 (when nginx is NOT actually in maintenance
 * mode) can't cause a reload loop: at most one reload per
 * `MAINTENANCE_RELOAD_MIN_INTERVAL_MS`.
 *
 * @param lastReloadAt the stored timestamp string, or null if none recorded yet
 * @param now current time in epoch ms
 */
export function shouldReloadForMaintenance(lastReloadAt: string | null, now: number): boolean {
  if (!lastReloadAt) {
    return true;
  }
  const last = parseInt(lastReloadAt, 10);
  if (Number.isNaN(last)) {
    return true;
  }
  return now - last >= MAINTENANCE_RELOAD_MIN_INTERVAL_MS;
}

/**
 * Session-level "maintenance is active" marker (FR-137, FR-138).
 *
 * A 503 from any API call sets this marker so the router-level MaintenanceGuard
 * can hand the visitor to the static maintenance page on their NEXT in-app
 * navigation — even one that makes no API call of its own and so would never
 * see a 503 on its own. A subsequent successful response clears it, so the app
 * self-heals once maintenance ends. Guarded against sessionStorage being
 * unavailable (private mode / disabled), consistent with FR-116a.
 */
export function markMaintenanceActive(): void {
  try {
    sessionStorage.setItem(MAINTENANCE_ACTIVE_KEY, '1');
  } catch {
    /* sessionStorage unavailable — the network probe still covers detection */
  }
}

/** Clear the maintenance marker (called on any successful response). */
export function clearMaintenanceActive(): void {
  try {
    sessionStorage.removeItem(MAINTENANCE_ACTIVE_KEY);
  } catch {
    /* sessionStorage unavailable — nothing to clear */
  }
}

/** Whether a prior 503 this session marked maintenance as currently active. */
export function isMaintenanceActive(): boolean {
  try {
    return sessionStorage.getItem(MAINTENANCE_ACTIVE_KEY) === '1';
  } catch {
    return false;
  }
}
